/**
 * Proje Gantt — Faz 30 (Work Item Bazlı)
 * =========================================
 * Work Item bazlı planlanan vs gerçekleşen tarih çubukları,
 * WBS + Lokasyon filtreleri.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ChartLine, ArrowLeft, Info, ChartLineUp, FilePdf, Camera, Trash, Warning, CheckCircle } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function parseISO(s) { return s ? new Date(s + "T00:00:00") : null; }
function diffDays(a, b) { return Math.max(1, Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1); }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

export default function ProjectGantt() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fWbs, setFWbs] = useState("all");
  const [fLoc, setFLoc] = useState("all");
  const [fSearch, setFSearch] = useState("");
  const [pageSize, setPageSize] = useState("A4");
  const [baselineMeta, setBaselineMeta] = useState(null);
  const [setOpen, setSetOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [baselineName, setBaselineName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/work-item-progress/gantt/${siteId}`);
      setRows(data.rows || []);
      setBaselineMeta(data.baseline || null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId]);

  const setBaseline = async () => {
    try {
      const { data } = await axios.post(`${API}/work-item-baselines/${siteId}`,
        { name: baselineName || "" });
      toast.success(`Baseline oluşturuldu: ${data.items} kalem`);
      setSetOpen(false);
      setBaselineName("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteBaseline = async () => {
    try {
      await axios.delete(`${API}/work-item-baselines/${siteId}`);
      toast.success("Baseline silindi");
      setDelOpen(false);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (fWbs !== "all") params.set("wbs_node_id", fWbs);
      if (fLoc !== "all") params.set("location_id", fLoc === "__root__" ? "" : fLoc);
      if (fSearch.trim()) params.set("search", fSearch.trim());
      params.set("page_size", pageSize);
      const url = `${API}/work-item-progress/gantt/${siteId}/pdf?${params}`;
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `gantt-${siteId}-${pageSize}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const wbsOptions = useMemo(() => {
    const seen = new Map();
    for (const r of rows) if (r.wbs_node_id && !seen.has(r.wbs_node_id))
      seen.set(r.wbs_node_id, r.wbs_path || r.name);
    return Array.from(seen.entries());
  }, [rows]);
  const locOptions = useMemo(() => {
    const seen = new Map();
    let hasRoot = false;
    for (const r of rows) {
      if (!r.location_id) { hasRoot = true; continue; }
      if (!seen.has(r.location_id)) seen.set(r.location_id, r.location_label);
    }
    return { list: Array.from(seen.entries()), hasRoot };
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (fWbs !== "all") out = out.filter((r) => r.wbs_node_id === fWbs);
    if (fLoc !== "all") {
      if (fLoc === "__root__") out = out.filter((r) => !r.location_id);
      else out = out.filter((r) => r.location_id === fLoc);
    }
    if (fSearch.trim()) {
      const q = fSearch.trim().toLocaleLowerCase("tr-TR");
      out = out.filter((r) =>
        (r.name || "").toLocaleLowerCase("tr-TR").includes(q) ||
        (r.wbs_path || "").toLocaleLowerCase("tr-TR").includes(q) ||
        (r.location_label || "").toLocaleLowerCase("tr-TR").includes(q));
    }
    return out;
  }, [rows, fWbs, fLoc, fSearch]);

  const range = useMemo(() => {
    if (!filtered.length) return null;
    let min = null, max = null;
    for (const r of filtered) {
      for (const dstr of [r.planned_start, r.planned_finish, r.actual_start, r.actual_finish]) {
        const d = parseISO(dstr);
        if (d) {
          if (!min || d < min) min = d;
          if (!max || d > max) max = d;
        }
      }
      // Devam eden aktivite: actual_start var, finish yok → bugünü kapsa
      if (r.actual_start && !r.actual_finish) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (!max || today > max) max = today;
      }
      // Baseline dates
      const b = r.baseline;
      if (b) {
        for (const dstr of [b.planned_start, b.planned_finish]) {
          const d = parseISO(dstr);
          if (d) {
            if (!min || d < min) min = d;
            if (!max || d > max) max = d;
          }
        }
      }
    }
    if (!min || !max) return null;
    min = addDays(min, -3);
    max = addDays(max, 3);
    return { min, max, days: diffDays(min, max) };
  }, [filtered]);

  const monthMarkers = useMemo(() => {
    if (!range) return [];
    const markers = [];
    let cur = new Date(range.min.getFullYear(), range.min.getMonth(), 1);
    while (cur <= range.max) {
      const dayOffset = Math.max(0, Math.round((cur - range.min) / (1000 * 60 * 60 * 24)));
      markers.push({
        label: cur.toLocaleDateString("tr-TR", { month: "short", year: "2-digit" }),
        offset: dayOffset,
      });
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    return markers;
  }, [range]);

  // Bugünkü tarih çizgisi — Gantt zaman aralığındaysa yüzde konumu döner
  const todayOffsetPct = useMemo(() => {
    if (!range) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (today < range.min || today > range.max) return null;
    const off = Math.round((today - range.min) / (1000 * 60 * 60 * 24));
    return (off / range.days) * 100;
  }, [range]);

  const barGeom = (row) => {
    if (!range) return null;
    const dayWidth = 100 / range.days;
    const geo = (s, e) => {
      const ds = parseISO(s), de = parseISO(e);
      if (!ds || !de) return null;
      const so = Math.max(0, Math.round((ds - range.min) / (1000 * 60 * 60 * 24)));
      const eo = Math.max(so, Math.round((de - range.min) / (1000 * 60 * 60 * 24)));
      return { left: so * dayWidth, width: (eo - so + 1) * dayWidth };
    };
    let actual = geo(row.actual_start, row.actual_finish);
    let actualOngoing = false;
    if (!actual && row.actual_start && !row.actual_finish) {
      // Devam ediyor: bugüne kadar
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      actual = geo(row.actual_start, today.toISOString().slice(0, 10));
      actualOngoing = !!actual;
    }
    return {
      planned: geo(row.planned_start, row.planned_finish),
      actual,
      actualOngoing,
      baseline: row.baseline
        ? geo(row.baseline.planned_start, row.baseline.planned_finish)
        : null,
    };
  };

  return (
    <Shell
      title="Gantt — Planlanan vs Gerçekleşen"
      subtitle="Proje İlerleme › Zaman Çizelgesi (Work Item)"
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          {baselineMeta ? (
            <Button variant="outline" onClick={() => setDelOpen(true)}
                    data-testid="gantt-baseline-del"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest border-red-300 text-red-700 hover:bg-red-50">
              <Trash size={14} className="mr-1.5" /> Baseline Sil
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setSetOpen(true)}
                    data-testid="gantt-baseline-set"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest border-[#95C11F] text-[#556B10] hover:bg-[#F7FBE9]">
              <Camera size={14} className="mr-1.5" /> Baseline Ayarla
            </Button>
          )}
          <Select value={pageSize} onValueChange={setPageSize}>
            <SelectTrigger data-testid="gantt-page-size" className="h-9 w-24 rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="A4">A4</SelectItem>
              <SelectItem value="A3">A3</SelectItem>
              <SelectItem value="A2">A2</SelectItem>
              <SelectItem value="A1">A1</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="gantt-pdf-btn" onClick={downloadPdf}
                  disabled={rows.length === 0}
                  className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest bg-[#1B4776] hover:bg-[#153859] text-white">
            <FilePdf size={14} className="mr-1.5" /> PDF İndir
          </Button>
          <Link to={`/proje/${siteId}/ilerleme`}>
            <Button variant="outline" data-testid="gantt-go-progress"
                    className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
              <ChartLineUp size={14} className="mr-1.5" /> İlerleme
            </Button>
          </Link>
          <Button variant="outline" onClick={() => navigate(`/santiyeler`)}
                  className="rounded-none sm:rounded-sm h-9 text-xs uppercase tracking-widest">
            <ArrowLeft size={14} className="mr-1.5" /> Şantiyeler
          </Button>
        </div>
      }
    >
      {baselineMeta && (
        <Card className="p-3 rounded-none sm:rounded-sm mb-3 border-l-4 border-[#95C11F] bg-[#F7FBE9]/40" data-testid="gantt-baseline-banner">
          <div className="flex items-center gap-3 flex-wrap">
            <Camera size={18} weight="duotone" className="text-[#556B10]" />
            <div className="text-xs flex-1">
              <div className="font-bold text-slate-900">📌 Baseline Aktif — {baselineMeta.name}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {baselineMeta.source_year_month && <>Kaynak Ay: <span className="font-mono">{baselineMeta.source_year_month}</span> · </>}
                {baselineMeta.created_by_name} · {new Date(baselineMeta.created_at).toLocaleString("tr-TR")}
              </div>
            </div>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[#95C11F] inline-block"></span> Zamanında</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 inline-block"></span> Dikkat (&le;7g)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-600 inline-block"></span> Gecikme</span>
            </div>
          </div>
        </Card>
      )}
      <Card className="p-3 rounded-none sm:rounded-sm mb-3 flex flex-wrap items-center gap-3">
        <ChartLine size={20} weight="duotone" className="text-[#95C11F]" />
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 bg-[#1B4776]"></div>
            <span className="text-slate-600">Planlanan</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 bg-[#95C11F]"></div>
            <span className="text-slate-600">Gerçekleşen</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 bg-[#95C11F]/40 border border-dashed border-[#95C11F]"></div>
            <span className="text-slate-600">Devam Ediyor</span>
          </div>
          {baselineMeta && (
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-1.5 bg-slate-400"></div>
              <span className="text-slate-600">Baseline</span>
            </div>
          )}
        </div>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <Input
          data-testid="gantt-search"
          placeholder="Ara: iş kalemi..."
          value={fSearch}
          onChange={(e) => setFSearch(e.target.value)}
          className="h-8 w-56 rounded-none sm:rounded-sm text-xs" />
        <Select value={fWbs} onValueChange={setFWbs}>
          <SelectTrigger data-testid="gantt-filter-wbs" className="h-8 w-56 rounded-none sm:rounded-sm text-xs">
            <SelectValue placeholder="WBS" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm WBS</SelectItem>
            {wbsOptions.map(([id, label]) => (
              <SelectItem key={id} value={id}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={fLoc} onValueChange={setFLoc}>
          <SelectTrigger data-testid="gantt-filter-loc" className="h-8 w-56 rounded-none sm:rounded-sm text-xs">
            <SelectValue placeholder="Lokasyon" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Lokasyonlar</SelectItem>
            {locOptions.hasRoot && <SelectItem value="__root__">— (Proje Kökü)</SelectItem>}
            {locOptions.list.map(([id, label]) => (
              <SelectItem key={id} value={id}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Info size={13} className="text-slate-400" />
        <div className="text-[10px] text-slate-500 italic">
          Tarihleri güncellemek için Proje İlerleme'yi kullanın.
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : rows.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm text-center border-dashed border-2 border-slate-200">
          <ChartLine size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          <div className="text-sm font-bold text-slate-700 mb-1">Zaman çizelgesi için veri yok</div>
          <div className="text-xs text-slate-500">
            Bu şantiyede Work Item yok. Önce Work Item Yönetimi'nden kalem oluşturun.
          </div>
          <Button variant="outline" onClick={() => navigate(`/proje/${siteId}/work-items`)}
                  data-testid="gantt-empty-goto-wi"
                  className="mt-4 rounded-none sm:rounded-sm text-xs uppercase tracking-widest">
            Work Item Yönetimi
          </Button>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 rounded-none sm:rounded-sm text-center border-dashed border-2 border-slate-200">
          <div className="text-sm font-bold text-slate-700 mb-1">Filtreye uyan kayıt yok</div>
          <div className="text-xs text-slate-500">Filtreleri temizleyerek tekrar deneyin.</div>
        </Card>
      ) : (
        <Card className="rounded-none sm:rounded-sm overflow-hidden" data-testid="gantt-card">
          {!range && (
            <div className="px-3 py-2 border-b border-amber-200 bg-amber-50 text-[11px] text-amber-800">
              Bu sonuç grubunda hiçbir Work Item için planlı/gerçekleşen tarih girilmemiş.
              Tarih bilgisini <b>Proje İlerleme</b> sayfasından girin.
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-100 z-10 min-w-[260px]">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-600">İş Kalemi / WBS / Lokasyon</span>
                  </th>
                  <th className="text-left px-3 py-2 relative min-w-[600px]">
                    <div className="relative h-4">
                      {(monthMarkers || []).map((m, i) => (
                        <div key={i}
                             className="absolute top-0 text-[9px] uppercase tracking-widest font-bold text-slate-500 border-l border-slate-300 pl-1"
                             style={{ left: `${range ? (m.offset / range.days) * 100 : 0}%` }}>
                          {m.label}
                        </div>
                      ))}
                      {todayOffsetPct != null && (
                        <div
                          data-testid="gantt-today-marker"
                          className="absolute -top-1 flex items-center gap-0.5 pointer-events-none"
                          style={{ left: `${todayOffsetPct}%`, transform: "translateX(-50%)" }}
                        >
                          <span className="text-[9px] uppercase tracking-widest font-bold text-rose-600 bg-white px-1 border border-rose-300">
                            Bugün
                          </span>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="text-right px-3 py-2 min-w-[80px] text-[10px] uppercase tracking-widest font-bold text-slate-600">İlerleme</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const g = barGeom(r);
                  const bl = r.baseline;
                  const statusCol = bl ? ({
                    on_track: "#95C11F", watch: "#f59e0b", delayed: "#dc2626",
                  })[bl.status] : null;
                  return (
                    <tr key={r.work_item_id} data-testid={`gantt-row-${i}`}
                        className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-1.5 sticky left-0 bg-white z-[1] min-w-[260px] relative">
                        {statusCol && (
                          <div className="absolute left-0 top-0 bottom-0 w-1"
                               style={{ backgroundColor: statusCol }}
                               data-testid={`gantt-row-status-${i}`}
                               title={{on_track:"Zamanında", watch:"Dikkat", delayed:"Gecikme"}[bl.status]} />
                        )}
                        <div className={`font-medium text-slate-900 text-xs truncate ${statusCol ? "pl-2" : ""}`} title={r.name}>
                          {r.name}
                        </div>
                        <div className={`text-[10px] text-slate-500 truncate ${statusCol ? "pl-2" : ""}`} title={r.wbs_path}>
                          WBS: {r.wbs_path || "-"}
                        </div>
                        <div className={`text-[10px] text-slate-500 truncate ${statusCol ? "pl-2" : ""}`} title={r.location_label}>
                          Yer: {r.location_label || "-"}
                        </div>
                        {bl && (bl.finish_variance_days != null || bl.progress_variance_pct !== 0) && (
                          <div className={`text-[10px] mt-0.5 font-mono ${statusCol ? "pl-2" : ""}`}
                               style={{ color: statusCol || "#64748b" }}>
                            {bl.finish_variance_days != null && (
                              <span title="Bitiş sapması">Δbit: {bl.finish_variance_days > 0 ? "+" : ""}{bl.finish_variance_days}g</span>
                            )}
                            {bl.progress_variance_pct !== 0 && (
                              <span className="ml-2" title="İlerleme sapması">
                                Δ%: {bl.progress_variance_pct > 0 ? "+" : ""}{bl.progress_variance_pct}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1.5 min-w-[600px]">
                        <div className="relative h-8 bg-slate-50">
                          {g?.baseline && (
                            <div className="absolute top-0.5 h-1.5 bg-slate-400 rounded-sm opacity-70"
                                 style={{ left: `${g.baseline.left}%`, width: `${g.baseline.width}%` }}
                                 title={`Baseline: ${bl?.planned_start} → ${bl?.planned_finish}`} />
                          )}
                          {g?.planned && (
                            <div className="absolute h-3 bg-[#1B4776] opacity-90"
                                 style={{
                                   top: g?.baseline ? "8px" : "2px",
                                   left: `${g.planned.left}%`, width: `${g.planned.width}%`,
                                 }}
                                 title={`Planlanan: ${r.planned_start} → ${r.planned_finish}`} />
                          )}
                          {g?.actual && (
                            <div className={`absolute bottom-0.5 h-3 ${
                              g.actualOngoing
                                ? "bg-[#95C11F]/40 border-2 border-dashed border-[#95C11F]"
                                : "bg-[#95C11F]"}`}
                                 style={{ left: `${g.actual.left}%`, width: `${g.actual.width}%` }}
                                 title={g.actualOngoing
                                    ? `Devam ediyor — ${r.actual_start} → (bugün)`
                                    : `Gerçekleşen: ${r.actual_start} → ${r.actual_finish || "—"}`} />
                          )}
                          {g?.actualOngoing && (
                            <div className="absolute bottom-0.5 h-3 flex items-center justify-center pointer-events-none"
                                 style={{ left: `${g.actual.left + g.actual.width}%`, width: "auto" }}>
                              <span className="text-[9px] font-bold text-[#556B10] uppercase tracking-widest ml-1 whitespace-nowrap">
                                ▶ Devam
                              </span>
                            </div>
                          )}
                          {!g?.planned && !g?.actual && !g?.baseline && (
                            <div className="absolute inset-0 flex items-center justify-center text-[9px] uppercase tracking-widest text-slate-300">
                              Tarih yok
                            </div>
                          )}
                          {todayOffsetPct != null && (
                            <div
                              data-testid={`gantt-today-line-${i}`}
                              className="absolute inset-y-0 w-px bg-rose-500 pointer-events-none"
                              style={{ left: `${todayOffsetPct}%` }}
                              title="Bugün"
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-xs font-bold"
                          style={{ color: r.progress_percent >= 100 ? "#95C11F" : r.progress_percent > 0 ? "#1B4776" : "#94a3b8" }}>
                        %{Number(r.progress_percent || 0).toFixed(0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Baseline Set Dialog */}
      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase tracking-widest text-sm">Baseline Ayarla</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-[11px] text-slate-500 bg-slate-50 p-2 border-l-2 border-[#95C11F]">
              Mevcut ilerleme durumunun snapshot'ı referans olarak kaydedilir.
              Bundan sonra Gantt'ta <b>planlı</b> tarihler değiştikçe gri baseline barı olduğu
              yerde kalır ve sapma <span className="text-emerald-700">yeşil</span> /
              <span className="text-amber-600"> sarı</span> /
              <span className="text-red-700"> kırmızı</span> renklerle gösterilir.
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
                Baseline Adı (opsiyonel)
              </label>
              <Input data-testid="gantt-baseline-name"
                     placeholder="örn: Sözleşme Programı v1"
                     value={baselineName}
                     onChange={(e) => setBaselineName(e.target.value)}
                     className="h-9 rounded-none sm:rounded-sm mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetOpen(false)}
                    className="rounded-none sm:rounded-sm uppercase text-xs tracking-widest">İptal</Button>
            <Button onClick={setBaseline} data-testid="gantt-baseline-set-confirm"
                    className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7ea01a] text-white uppercase text-xs tracking-widest">
              <Camera size={13} className="mr-1" /> Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Baseline Delete Alert */}
      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="rounded-none sm:rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="uppercase tracking-widest text-sm">Baseline Sil</AlertDialogTitle>
            <AlertDialogDescription>
              "<b>{baselineMeta?.name}</b>" baseline'ı silinecek. Devam etmek istiyor musunuz?
              Bu işlem geri alınamaz. Yeni bir baseline ayarlayabilirsiniz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none sm:rounded-sm">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={deleteBaseline}
                               data-testid="gantt-baseline-del-confirm"
                               className="rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}
